/**
 * Anthropic Messages API adapter for the Excel multi-hop executor.
 *
 * Where the OpenAI Responses API accepts a flat list of items in any order,
 * Messages requires a strictly alternating `user` / `assistant` transcript of
 * whole messages, each holding an ordered array of content blocks. The three
 * structural consequences handled here are:
 *
 *  1. Runs of `tool_result` and `user` turns collapse into ONE user message
 *     (`encodeTurns`), with the tool results ahead of the orchestrator text so
 *     they directly follow the assistant turn that requested them.
 *  2. Compaction operates on whole messages rather than single items, so the
 *     recent-window budget is re-derived in message pairs (`projectItems`).
 *  3. Assistant turns are replayed verbatim, thinking blocks and their
 *     signatures intact. Anthropic rejects a tool-use turn whose assistant
 *     message lost its thinking blocks, and the signature is what lets the
 *     server reconstruct the original reasoning.
 */

import { buildApiUrl } from "../../shared/config";
import { streamAnthropicTurn } from "../../shared/anthropic/messagesStream";
import {
  buildCachedSystem,
  withCachedToolDefinitions,
  withRollingCacheBreakpoint,
} from "../../shared/anthropic/promptCaching";
import { ADAPTIVE_THINKING, toAnthropicEffort } from "../../shared/anthropic/thinking";
import { buildToolDefinitionsForProvider } from "../toolRegistry";
import {
  MAX_REPLAYED_TOOL_OUTPUT_CHARS,
  truncateReplayedToolOutput,
} from "./replayTruncation";
import type {
  AgentTurn,
  AgentTurnResult,
  ExecutorBuildRequestParams,
  ExecutorProviderAdapter,
  ExecutorProviderRequest,
  ExecutorSerializeToolsParams,
  ExecutorStreamTurnParams,
} from "./types";

// ---------------------------------------------------------------------------
// Messages API types (minimal)
// ---------------------------------------------------------------------------

type AnthropicContentBlock = Record<string, unknown>;

interface AnthropicMessage {
  role: "user" | "assistant";
  content: AnthropicContentBlock[];
}

/** Stand-in output for a `tool_use` the runner never reported a result for. */
const UNRESOLVED_TOOL_RESULT_OUTPUT =
  "This tool call produced no result (the run moved on before it completed).";

/** Keeps a merged user message from being emitted with zero content blocks. */
const EMPTY_USER_MESSAGE_PLACEHOLDER = "Continue.";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function blockType(block: unknown): string {
  return isRecord(block) && typeof block.type === "string" ? block.type : "";
}

// ---------------------------------------------------------------------------
// Turn encoding
// ---------------------------------------------------------------------------

/**
 * One in-progress message. User groups keep tool results separate from the rest
 * so the flattened content always leads with `tool_result` blocks, whatever
 * order the runner appended its turns in.
 */
interface MessageGroup {
  role: "user" | "assistant";
  toolResults: AnthropicContentBlock[];
  rest: AnthropicContentBlock[];
}

function flattenGroup(group: MessageGroup): AnthropicMessage {
  const content = [...group.toolResults, ...group.rest];
  if (content.length === 0 && group.role === "user") {
    content.push({ type: "text", text: EMPTY_USER_MESSAGE_PLACEHOLDER });
  }
  return { role: group.role, content };
}

/**
 * Rewrites an orphaned `tool_result` (no matching `tool_use` in the preceding
 * assistant message) as plain text. Anthropic rejects the unpaired block, but
 * the output itself is still context the model should see.
 */
function orphanedToolResultAsText(block: AnthropicContentBlock): AnthropicContentBlock {
  const id = typeof block.tool_use_id === "string" ? block.tool_use_id : "unknown";
  const content = block.content;
  const rendered =
    typeof content === "string" ? content : JSON.stringify(content ?? null);
  return { type: "text", text: `Tool result (${id}): ${rendered}` };
}

/**
 * Enforces the two pairing rules the Messages API validates: every `tool_use`
 * in an assistant message must be answered by a `tool_result` at the head of
 * the next user message, and no `tool_result` may reference a `tool_use` that
 * is not in the message immediately before it.
 *
 * The runner appends a result for every call it dispatches, so this is a
 * backstop: an unpaired block would fail the whole request with a 400 rather
 * than degrade, which is far too expensive mid-run.
 */
function repairToolPairing(messages: AnthropicMessage[]): AnthropicMessage[] {
  const repaired: AnthropicMessage[] = [];
  for (let i = 0; i < messages.length; i += 1) {
    const message = messages[i];
    if (message.role === "user") {
      const previous = repaired[repaired.length - 1];
      const openIds = new Set(
        previous?.role === "assistant"
          ? previous.content
              .filter((block) => blockType(block) === "tool_use")
              .map((block) => String(block.id ?? ""))
          : [],
      );
      const content = message.content.map((block) =>
        blockType(block) === "tool_result" &&
        !openIds.has(String(block.tool_use_id ?? ""))
          ? orphanedToolResultAsText(block)
          : block,
      );
      repaired.push({ role: "user", content });
      continue;
    }
    repaired.push(message);

    const pendingIds = message.content
      .filter((block) => blockType(block) === "tool_use")
      .map((block) => String(block.id ?? ""))
      .filter(Boolean);
    if (pendingIds.length === 0) continue;

    const next = messages[i + 1];
    const answeredIds = new Set(
      next?.role === "user"
        ? next.content
            .filter((block) => blockType(block) === "tool_result")
            .map((block) => String(block.tool_use_id ?? ""))
        : [],
    );
    const stubs = pendingIds
      .filter((id) => !answeredIds.has(id))
      .map((id) => ({
        type: "tool_result",
        tool_use_id: id,
        content: UNRESOLVED_TOOL_RESULT_OUTPUT,
        is_error: true,
      }));
    if (stubs.length === 0) continue;
    if (next?.role === "user") {
      // Consumed on the next iteration, which re-reads `messages[i + 1]`.
      next.content = [...stubs, ...next.content];
    } else {
      messages.splice(i + 1, 0, { role: "user", content: stubs });
    }
  }
  return repaired;
}

/**
 * Flattens the canonical IR into an alternating Messages transcript.
 *
 * Model turns are spread back in verbatim so signed thinking blocks survive the
 * round-trip. Every other turn contributes to the current user group, which is
 * only closed when the next assistant turn arrives. Snapshot references become
 * native base64 image blocks inside their matching tool result.
 *
 * @param turns - Canonical provider-neutral conversation turns.
 * @returns Alternating Anthropic messages with valid tool-use/result pairing.
 */
function encodeTurns(turns: readonly AgentTurn[]): unknown[] {
  const groups: MessageGroup[] = [];
  /**
   * Return the active message group for a role, creating one when necessary.
   *
   * @param role - Anthropic message role being encoded.
   * @returns Mutable message group for that role.
   */
  const groupFor = (role: "user" | "assistant"): MessageGroup => {
    const last = groups[groups.length - 1];
    if (last && last.role === role) return last;
    const group: MessageGroup = { role, toolResults: [], rest: [] };
    groups.push(group);
    return group;
  };

  for (const turn of turns) {
    if (turn.kind === "model") {
      const group = groupFor("assistant");
      for (const block of turn.raw) {
        if (isRecord(block)) group.rest.push(block);
      }
      continue;
    }
    if (turn.kind === "tool_result") {
      groupFor("user").toolResults.push({
        type: "tool_result",
        tool_use_id: turn.callId,
        content: turn.referenceImages?.length
          ? [
              { type: "text", text: turn.output },
              ...turn.referenceImages.flatMap((image) => [
                { type: "text", text: image.description },
                {
                  type: "image",
                  source: {
                    type: "base64",
                    media_type: image.mimeType,
                    data: image.base64,
                  },
                },
              ]),
            ]
          : turn.output,
      });
      continue;
    }
    groupFor("user").rest.push({ type: "text", text: turn.content });
  }

  // An assistant turn is only empty if the provider returned nothing. Dropping
  // it can leave two user messages adjacent, so re-merge by role afterwards to
  // keep the transcript strictly alternating.
  const messages: AnthropicMessage[] = [];
  for (const message of groups.map(flattenGroup)) {
    if (message.content.length === 0) continue;
    const last = messages[messages.length - 1];
    if (last && last.role === message.role) {
      last.content.push(...message.content);
      continue;
    }
    messages.push(message);
  }
  return repairToolPairing(messages);
}

// ---------------------------------------------------------------------------
// History compaction
// ---------------------------------------------------------------------------

/**
 * Recent-window budget, re-derived for whole messages.
 *
 * The OpenAI adapter keeps the last 12 Responses ITEMS, roughly four hops —
 * fewer once the model calls tools in parallel, since each extra call adds two
 * more items.
 *
 * One encoded Anthropic item is a whole message, and a hop is always exactly
 * two of them (assistant + merged user), however many tools the turn called.
 * Eight messages therefore reproduces the same four-hop verbatim window and,
 * unlike the item count, does not shrink under parallel tool use. Everything
 * older is still replayed in full; only its oversized tool outputs shrink.
 */
const RECENT_MESSAGES_KEPT_FULL = 8;
/** Maximum provider-native images retained in one replay request. */
const MAX_REPLAYED_IMAGES = 8;
/** Maximum decoded image bytes retained in one replay request. */
const MAX_REPLAYED_IMAGE_BYTES = 20_000_000;
/** Model-visible marker substituted for an older omitted image. */
const OMITTED_IMAGE_TEXT =
  "[Earlier snapshot image omitted from replay to limit request size. Call get_snapshot again if it is still needed.]";

/**
 * Estimate decoded bytes for an Anthropic native base64 image block.
 *
 * @param value - Candidate Anthropic content block.
 * @returns Decoded byte length, or `-1` when the value is not a base64 image.
 */
function anthropicImageByteLength(value: unknown): number {
  if (!isRecord(value) || blockType(value) !== "image" || !isRecord(value.source)) return -1;
  const source = value.source as Record<string, unknown>;
  if (source.type !== "base64" || typeof source.data !== "string") return -1;
  const padding = source.data.endsWith("==") ? 2 : source.data.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((source.data.length * 3) / 4) - padding);
}

/**
 * Collect image-block object identities in request traversal order.
 *
 * @param value - Encoded message subtree to inspect.
 * @param images - Mutable destination receiving discovered image blocks.
 * @returns Nothing.
 */
function collectImages(value: unknown, images: object[]): void {
  if (Array.isArray(value)) {
    for (const item of value) collectImages(item, images);
    return;
  }
  if (!isRecord(value)) return;
  if (anthropicImageByteLength(value) >= 0) {
    images.push(value);
    return;
  }
  for (const nested of Object.values(value)) collectImages(nested, images);
}

/**
 * Replace image blocks outside the retained set with compact text markers.
 *
 * @param value - Encoded message subtree to project.
 * @param retained - Image object identities allowed to keep their bytes.
 * @returns Original subtree when unchanged, otherwise a copy with omissions.
 */
function replaceOmittedImages(value: unknown, retained: WeakSet<object>): unknown {
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((item) => {
      const replacement = replaceOmittedImages(item, retained);
      if (replacement !== item) changed = true;
      return replacement;
    });
    return changed ? next : value;
  }
  if (!isRecord(value)) return value;
  if (anthropicImageByteLength(value) >= 0 && !retained.has(value)) {
    return { type: "text", text: OMITTED_IMAGE_TEXT };
  }
  let changed = false;
  const entries = Object.entries(value).map(([key, nested]) => {
    const replacement = replaceOmittedImages(nested, retained);
    if (replacement !== nested) changed = true;
    return [key, replacement];
  });
  return changed ? Object.fromEntries(entries) : value;
}

/**
 * Keep the newest bounded set of images without mutating canonical messages.
 *
 * @param items - Encoded Anthropic messages.
 * @returns Projected messages with older images replaced by text markers.
 */
function pruneOlderImages(items: readonly unknown[]): unknown[] {
  const images: object[] = [];
  collectImages(items, images);
  const retained = new WeakSet<object>();
  let retainedCount = 0;
  let retainedBytes = 0;
  for (let index = images.length - 1; index >= 0; index -= 1) {
    const image = images[index];
    const bytes = anthropicImageByteLength(image);
    if (
      retainedCount < MAX_REPLAYED_IMAGES &&
      retainedBytes + bytes <= MAX_REPLAYED_IMAGE_BYTES
    ) {
      retained.add(image);
      retainedCount += 1;
      retainedBytes += bytes;
    }
  }
  return replaceOmittedImages(items, retained) as unknown[];
}

/**
 * Compacts one older message by shrinking large tool outputs.
 *
 * Note what is deliberately NOT dropped: thinking blocks. The OpenAI adapter
 * discards superseded `reasoning` items, but the equivalent saving is not
 * available here and not worth chasing. Anthropic validates that a replayed
 * assistant message arrives unmodified and rejects a rebuilt one, it filters
 * older thinking server-side anyway, and it bills only for the blocks it
 * actually puts in context. Removing them client-side risks a hard 400 in
 * exchange for tokens the API was already not charging for.
 *
 * Returns the original object when nothing changed, so the projection stays
 * cheap and mostly reference-identical.
 *
 * @param item - Older encoded Anthropic message candidate.
 * @returns Original message or a copy with oversized tool output truncated.
 */
function compactOlderMessage(item: unknown): unknown {
  if (!isRecord(item) || !Array.isArray(item.content)) return item;
  let changed = false;
  const content = item.content.map((block) => {
    if (blockType(block) !== "tool_result" || !isRecord(block)) return block;
    const output = block.content;
    if (typeof output !== "string" || output.length <= MAX_REPLAYED_TOOL_OUTPUT_CHARS) {
      return block;
    }
    changed = true;
    return { ...block, content: truncateReplayedToolOutput(output) };
  });
  return changed ? { ...item, content } : item;
}

/**
 * Builds the compacted transcript sent on the next request while leaving the
 * canonical conversation untouched. The seed user message (request + plan +
 * workbook context) and the recent window keep their non-image content
 * verbatim; snapshot images remain subject to the global replay budget.
 *
 * @param items - Fully encoded canonical Anthropic messages.
 * @returns Compacted request copy safe to send on the next model turn.
 */
function projectItems(items: readonly unknown[]): unknown[] {
  const cutoff = Math.max(0, items.length - RECENT_MESSAGES_KEPT_FULL);
  const compacted = items.map((item, index) =>
    index === 0 || index >= cutoff ? item : compactOlderMessage(item),
  );
  return pruneOlderImages(compacted);
}

// ---------------------------------------------------------------------------
// Request building
// ---------------------------------------------------------------------------

/** `max_tokens` is mandatory on Messages, unlike OpenAI's optional cap. */
const DEFAULT_MAX_OUTPUT_TOKENS = 8192;

function buildRequest({
  model,
  instructions,
  items,
  tools,
  forcedToolName,
}: ExecutorBuildRequestParams): ExecutorProviderRequest {
  return {
    url: buildApiUrl("/api/ai/anthropic/messages"),
    body: {
      model: model.model,
      system: buildCachedSystem(instructions),
      messages: withRollingCacheBreakpoint(items),
      tools: withCachedToolDefinitions(tools),
      // Opus 5 accepts a named forced tool. Newer always-on adaptive models
      // require auto tool choice, so their catalog metadata deliberately
      // degrades this hint rather than sending a request they reject.
      tool_choice: forcedToolName && model.forcedToolChoice === "supported"
        ? { type: "tool", name: forcedToolName }
        : { type: "auto" },
      max_tokens: model.maxOutputTokens ?? DEFAULT_MAX_OUTPUT_TOKENS,
      thinking: ADAPTIVE_THINKING,
      output_config: { effort: toAnthropicEffort(model.reasoningEffort) },
      stream: true,
    },
  };
}

async function streamTurn(params: ExecutorStreamTurnParams): Promise<AgentTurnResult> {
  return streamAnthropicTurn(params);
}

export const anthropicExecutorAdapter: ExecutorProviderAdapter = {
  provider: "anthropic",
  serializeTools: ({ scope, extraTools, options }: ExecutorSerializeToolsParams) =>
    buildToolDefinitionsForProvider("anthropic", scope, extraTools, options ?? {}) ?? [],
  encodeTurns,
  projectItems,
  buildRequest,
  streamTurn,
};

export { AnthropicStreamError } from "../../shared/anthropic/messagesStream";
export { MAX_CACHE_BREAKPOINTS } from "../../shared/anthropic/promptCaching";
